import AmplifyButton from '@amplify/amplify-button';
import clsx from 'clsx';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AppsIconK1,
  PastWorkIconK1,
  PastWorkIconSecondFifth,
  ToDoIconK1,
  ToDoIconSecondFifth,
} from 'src/components/icons';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useGetProductsByEntitlement } from 'src/hooks/use-get-products-by-entitlement.hook';
import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { Theme } from 'src/interfaces/theme.types';
import AuthService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  setActiveAssignmentsView,
  useActiveAssignmentsViewSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import './elementary-header-buttons.scss';

type ElementaryHeaderButtonsProps = {
  theme: Theme;
};

export const ElementaryHeaderButtons = ({
  theme,
}: ElementaryHeaderButtonsProps) => {
  const { t } = useTranslation();

  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const { productsByEntitlement } = useGetProductsByEntitlement();

  const dispatch = useAppDispatch();

  const isTodoActive = activeAssignmentsView === AssignmentsViewOptions.TODO;
  const isPastWorkActive =
    activeAssignmentsView === AssignmentsViewOptions.PAST_WORK;
  const isAppsActive = activeAssignmentsView === AssignmentsViewOptions.APPS;

  const isTeacher = AuthService.isTeacher();

  const sendFilteredXapiEvent = (
    isRightClick: boolean,
    activeStatus: AssignmentsViewOptions,
  ) => {
    eventService.send(
      XApiEventKeys.selectedStatusFilter,
      {
        result: {
          statusFiltersSelected: activeStatus,
        },
      },
      {
        result: {
          isRightClick,
          ...commonXapiContextPayload,
        },
      },
    );
  };

  const handleAppsBtnClick = (e: MouseEvent) => {
    sendFilteredXapiEvent(
      e.type === 'contextmenu',
      AssignmentsViewOptions.APPS,
    );

    if (!isAppsActive && e.type !== 'contextmenu') {
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.APPS));
    }
  };

  const handleToDoFilterBtnClick = (e: MouseEvent) => {
    sendFilteredXapiEvent(
      e.type === 'contextmenu',
      AssignmentsViewOptions.TODO,
    );

    if (!isTodoActive && e.type !== 'contextmenu') {
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.TODO));
    }
  };

  const handlePastWorkFilterBtnClick = (e: MouseEvent) => {
    sendFilteredXapiEvent(
      e.type === 'contextmenu',
      AssignmentsViewOptions.PAST_WORK,
    );

    if (!isPastWorkActive && e.type !== 'contextmenu') {
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.PAST_WORK));
    }
  };

  return (
    <div
      className={
        theme === Theme.K1 ? 'layout-context-bravo' : 'layout-context-delta'
      }
    >
      {productsByEntitlement.length ? (
        <AmplifyButton
          icon={theme !== Theme.K1 ? 'icon-k5-apps' : ''}
          onClick={handleAppsBtnClick}
          variant={isAppsActive ? 'primary' : 'secondary'}
          className={clsx('elementary-header-button', {
            'btn-raised': theme !== Theme.FOURTH_FIFTH,
            'elementary-header-button--k1': theme === Theme.K1,
            'elementary-header-button--k1-teacher':
              theme === Theme.K1 && isTeacher,
            'elementary-header-button--k1-apps': theme === Theme.K1,
          })}
        >
          {theme === Theme.K1 && <AppsIconK1 />}
          <span className="elementary-header-button__text">{t('Apps')}</span>
        </AmplifyButton>
      ) : null}
      <AmplifyButton
        onClick={handleToDoFilterBtnClick}
        variant={isTodoActive ? 'primary' : 'secondary'}
        className={clsx('elementary-header-button', {
          'btn-raised': theme !== Theme.FOURTH_FIFTH,
          'elementary-header-button--k1': theme === Theme.K1,
          'elementary-header-button--k1-teacher':
            theme === Theme.K1 && isTeacher,
          'elementary-header-button--k1-todo': theme === Theme.K1,
          'elementary-header-button--second-fifth':
            theme === Theme.SECOND_THIRD || theme === Theme.FOURTH_FIFTH,
        })}
        data-testid="todo-button"
      >
        {theme === Theme.K1 ? (
          <ToDoIconK1 />
        ) : (
          <ToDoIconSecondFifth color={isTodoActive ? '#fff' : '#0069E4'} />
        )}
        <span className="elementary-header-button__text">{t('To Do')}</span>
      </AmplifyButton>
      <AmplifyButton
        onClick={handlePastWorkFilterBtnClick}
        variant={isPastWorkActive ? 'primary' : 'secondary'}
        className={clsx('elementary-header-button', {
          'btn-raised': theme !== Theme.FOURTH_FIFTH,
          'elementary-header-button--k1': theme === Theme.K1,
          'elementary-header-button--k1-teacher':
            theme === Theme.K1 && isTeacher,
          'elementary-header-button--second-fifth':
            theme === Theme.SECOND_THIRD || theme === Theme.FOURTH_FIFTH,
        })}
      >
        {theme === Theme.K1 ? (
          <PastWorkIconK1 />
        ) : (
          <PastWorkIconSecondFifth
            color={isPastWorkActive ? '#fff' : '#0069E4'}
          />
        )}
        <span className="elementary-header-button__text">{t('Past Work')}</span>
      </AmplifyButton>
    </div>
  );
};
